/**
 * 12-Week Bodyweight Foundation Program
 * MINIMAL EQUIPMENT: Bodyweight only (pullup bar optional)
 * Perfect for home workouts, travel, or limited equipment
 * Progressive overload through volume, tempo, and exercise variations
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'bodyweight-foundation-12week',
  name: 'Bodyweight Foundation',
  description: 'Zero equipment needed! Build strength, conditioning, and durability using only your bodyweight. Pullup bar optional.',
  level: 'recruit',
  duration: 12,
  daysPerWeek: 4,
  goals: ['strength', 'conditioning', 'bodyweight-mastery', 'fat-loss'],
  difficulty: 'beginner',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
  requiredEquipment: 'minimal', // CRITICAL: Flags this as bodyweight-only
};

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    {
      weeks: [1],
      type: 'assessment',
      name: 'Baseline Testing',
      focus: 'Establish movement quality and capacity',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    },
    {
      weeks: [2, 3, 4],
      type: 'accumulation',
      name: 'Foundation Building',
      focus: 'Build work capacity and movement quality',
      intensityMultiplier: 0.9,
      volumeMultiplier: 1.0,
      trainingParameters: {
        repRange: [8, 15],
        setsMultiplier: 1.0,
        restPeriod: '45-60s'
      }
    },
    {
      weeks: [5, 6, 7, 8],
      type: 'accumulation',
      name: 'Volume Build',
      focus: 'Progressive volume increases and tempo work',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.15,
      trainingParameters: {
        repRange: [10, 20],
        setsMultiplier: 1.1,
        restPeriod: '45-60s'
      }
    },
    {
      weeks: [9, 10, 11],
      type: 'intensification',
      name: 'Intensity Peak',
      focus: 'Advanced variations and higher difficulty',
      intensityMultiplier: 1.15,
      volumeMultiplier: 0.9,
      trainingParameters: {
        repRange: [6, 12],
        setsMultiplier: 1.0,
        restPeriod: '60-90s'
      }
    },
    {
      weeks: [12],
      type: 'assessment',
      name: 'Progress Testing',
      focus: 'Measure improvements and plan next cycle',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    }
  ]
};

// ============================================================================
// WEEK 1: BASELINE TESTING
// ============================================================================

const week1Day1: WorkoutTemplateDay = {
  name: 'Bodyweight Assessment - Upper',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Jumping Jacks', sets: 1, reps: 30, format: 'prep' },
        { name: 'Arm Circles', sets: 1, reps: 20, format: 'prep', details: 'Forward and backward' },
        { name: 'Bodyweight Squats', sets: 1, reps: 15, format: 'prep' },
      ],
    },
    {
      name: 'Upper Body Testing',
      type: 'assessment',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', intensity: 'Max effort', details: 'Standard form, count max reps until failure', sessionType: 'assessment' },
        { name: 'Pull-Ups', sets: 1, reps: 'AMRAP', intensity: 'Max effort', details: 'If no bar: Australian pull-ups on table edge. Count max reps', sessionType: 'assessment' },
        { name: 'Pike Push-Ups', sets: 1, reps: 'AMRAP', intensity: 'Max effort', details: 'Hips high, count max reps for shoulder strength', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, duration: 'Max time', intensity: 'Max effort', details: 'Maintain perfect form - stop when form breaks', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        { name: 'Stretching', duration: '5-10 min', format: 'recovery', details: 'Focus on chest, shoulders, core' },
      ],
    },
  ],
};

const week1Day2: WorkoutTemplateDay = {
  name: 'Bodyweight Assessment - Lower',
  sessionType: 'assessment',
  estimatedDuration: 40,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'High Knees', sets: 1, reps: 30, format: 'prep' },
        { name: 'Leg Swings', sets: 1, reps: 15, format: 'prep', details: 'Each leg, front-to-back' },
        { name: 'Hip Circles', sets: 1, reps: 10, format: 'prep', details: 'Each direction' },
      ],
    },
    {
      name: 'Lower Body Testing',
      type: 'assessment',
      exercises: [
        { name: 'Air Squats', sets: 1, reps: 'AMRAP', intensity: 'Max effort', details: 'Full depth, count max reps in 2 minutes', sessionType: 'assessment' },
        { name: 'Lunges', sets: 1, reps: 'AMRAP', intensity: 'Max effort', details: 'Alternating legs, count total reps in 2 minutes', sessionType: 'assessment' },
        { name: 'Single-Leg Glute Bridge', sets: 1, reps: 'AMRAP', intensity: 'Max effort', details: 'Each leg - count max reps per leg', sessionType: 'assessment' },
        { name: 'Wall Sit', sets: 1, duration: 'Max time', intensity: 'Max effort', details: 'Hold 90-degree angle until failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        { name: 'Stretching', duration: '5-10 min', format: 'recovery', details: 'Focus on quads, hamstrings, glutes' },
      ],
    },
  ],
};

// ============================================================================
// WEEKS 2-4: FOUNDATION PHASE
// ============================================================================

const foundationUpperBody: WorkoutTemplateDay = {
  name: 'Upper Body Strength',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Arm Circles', sets: 1, reps: 20, format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Push Strength',
      type: 'strength',
      exercises: [
        { name: 'Push-Ups', sets: 3, reps: 10, intensity: 'Controlled tempo', rest: '60s', details: '2-0-2-0 tempo' },
        { name: 'Pike Push-Ups', sets: 3, reps: 8, intensity: 'Progressive', rest: '60s', details: 'Focus on shoulder strength' },
        { name: 'Diamond Push-Ups', sets: 3, reps: 8, intensity: 'Progressive', rest: '60s', details: 'Tricep emphasis' },
      ],
    },
    {
      name: 'Pull Strength',
      type: 'strength',
      exercises: [
        { name: 'Pull-Ups', sets: 3, reps: '6-8', intensity: 'Progressive', rest: '90s', details: 'Or Australian pull-ups if no bar' },
        { name: 'Inverted Rows', sets: 3, reps: 10, intensity: 'Progressive', rest: '60s', details: 'Under table or between chairs' },
      ],
    },
    {
      name: 'Core Finisher',
      type: 'core',
      exercises: [
        { name: 'Plank', sets: 3, duration: '45s', rest: '30s' },
        { name: 'Hollow Body Hold', sets: 3, duration: '30s', rest: '30s' },
      ],
    },
  ],
};

const foundationLowerBody: WorkoutTemplateDay = {
  name: 'Lower Body Strength',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Leg Swings', sets: 1, reps: 15, format: 'prep', details: 'Each leg' },
        { name: 'Air Squats', sets: 1, reps: 15, format: 'prep' },
      ],
    },
    {
      name: 'Lower Body Strength',
      type: 'strength',
      exercises: [
        { name: 'Bulgarian Split Squats', sets: 3, reps: 12, intensity: 'Bodyweight', rest: '60s', details: 'Each leg - use chair or bench' },
        { name: 'Single-Leg Romanian Deadlift', sets: 3, reps: 10, intensity: 'Bodyweight', rest: '60s', details: 'Each leg - balance focus' },
        { name: 'Jump Squats', sets: 3, reps: 10, intensity: 'Explosive', rest: '60s', details: 'Land softly, full depth' },
        { name: 'Walking Lunges', sets: 3, reps: 20, intensity: 'Controlled', rest: '45s', details: 'Total steps - keep torso upright' },
      ],
    },
    {
      name: 'Glute/Hamstring',
      type: 'accessory',
      exercises: [
        { name: 'Single-Leg Glute Bridge', sets: 3, reps: 12, intensity: 'Squeeze at top', rest: '45s', details: 'Each leg' },
        { name: 'Nordic Curls', sets: 3, reps: '5-8', intensity: 'Eccentric', rest: '90s', details: 'Hook feet under couch/bed. Slow negative' },
      ],
    },
  ],
};

const foundationConditioning: WorkoutTemplateDay = {
  name: 'Bodyweight Conditioning',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Jumping Jacks', sets: 1, reps: 30, format: 'prep' },
        { name: 'High Knees', sets: 1, reps: 30, format: 'prep' },
      ],
    },
    {
      name: 'Circuit Work',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Burpees', sets: 4, reps: 10, format: 'circuit', details: 'Full push-up at bottom' },
        { name: 'Jump Squats', sets: 4, reps: 15, format: 'circuit', details: 'Explosive, land soft' },
        { name: 'Mountain Climbers', sets: 4, reps: 30, format: 'circuit', details: 'Total reps - drive knees to chest' },
        { name: 'Plank to Down Dog', sets: 4, reps: 10, format: 'circuit', details: 'Dynamic movement' },
      ],
    },
    {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        { name: 'Walking', duration: '5 min', format: 'recovery' },
        { name: 'Stretching', duration: '5 min', format: 'recovery' },
      ],
    },
  ],
};

const foundationActive: WorkoutTemplateDay = {
  name: 'Active Recovery',
  sessionType: 'pt',
  estimatedDuration: 25,
  blocks: [
    {
      name: 'Light Movement',
      type: 'recovery',
      exercises: [
        { name: 'Walking or Light Jog', duration: '15-20 min', format: 'recovery', details: 'Easy pace, conversational' },
        { name: 'Yoga or Stretching', duration: '10-15 min', format: 'recovery', details: 'Focus on mobility and breath' },
      ],
    },
  ],
};

// ============================================================================
// WEEKS 5-8: VOLUME BUILD PHASE (INCREASED SETS)
// ============================================================================

const volumeUpperBody: WorkoutTemplateDay = {
  name: 'Upper Body Volume',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'prep', details: 'Or use towel for resistance' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 12, format: 'prep' },
      ],
    },
    {
      name: 'Push Volume',
      type: 'strength',
      exercises: [
        { name: 'Push-Ups', sets: 4, reps: 12, intensity: 'Progressive', rest: '60s', details: 'Add tempo variation: 3-0-1-0' },
        { name: 'Decline Push-Ups', sets: 4, reps: 10, intensity: 'Progressive', rest: '60s', details: 'Feet elevated on chair' },
        { name: 'Pike Push-Ups', sets: 4, reps: 10, intensity: 'Progressive', rest: '60s' },
      ],
    },
    {
      name: 'Pull Volume',
      type: 'strength',
      exercises: [
        { name: 'Pull-Ups', sets: 4, reps: '8-10', intensity: 'Progressive', rest: '90s', details: 'Or negatives if needed' },
        { name: 'Archer Push-Ups', sets: 3, reps: 8, intensity: 'Advanced', rest: '60s', details: 'Each side - single arm emphasis' },
      ],
    },
    {
      name: 'Core',
      type: 'core',
      exercises: [
        { name: 'L-Sit Hold', sets: 3, duration: '20-30s', rest: '45s', details: 'On floor or parallettes' },
        { name: 'Side Plank', sets: 3, duration: '30s', rest: '30s', details: 'Each side' },
      ],
    },
  ],
};

const volumeLowerBody: WorkoutTemplateDay = {
  name: 'Lower Body Volume',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Leg Swings', sets: 2, reps: 15, format: 'prep', details: 'Front-back and side-to-side' },
        { name: 'Cossack Squats', sets: 1, reps: 10, format: 'prep', details: 'Each side - hip mobility' },
      ],
    },
    {
      name: 'Quad/Glute Volume',
      type: 'strength',
      exercises: [
        { name: 'Pistol Squats', sets: 4, reps: '6-8', intensity: 'Advanced', rest: '90s', details: 'Each leg. Use assistance if needed' },
        { name: 'Bulgarian Split Squats', sets: 4, reps: 15, intensity: 'Tempo', rest: '60s', details: 'Each leg - 3-0-1-0 tempo' },
        { name: 'Jump Lunges', sets: 4, reps: 16, intensity: 'Explosive', rest: '60s', details: 'Total reps - switch in air' },
      ],
    },
    {
      name: 'Posterior Chain',
      type: 'strength',
      exercises: [
        { name: 'Single-Leg Glute Bridge', sets: 4, reps: 15, intensity: 'Squeeze', rest: '45s', details: 'Each leg - 2 second hold' },
        { name: 'Nordic Curls', sets: 4, reps: '6-10', intensity: 'Eccentric', rest: '90s', details: 'Control the negative' },
      ],
    },
  ],
};

const volumeConditioning: WorkoutTemplateDay = {
  name: 'High Volume Conditioning',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'EMOM (Every Minute on the Minute)',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Bodyweight EMOM', 
          sets: 12, 
          reps: 'See details', 
          format: 'emom',
          details: 'Min 1: 10 Burpees | Min 2: 20 Air Squats | Min 3: 15 Push-Ups | Repeat 4x' 
        },
      ],
    },
    {
      name: 'Finisher',
      type: 'conditioning',
      exercises: [
        { name: 'Mountain Climbers', sets: 3, reps: 50, rest: '60s', details: 'Fast pace' },
        { name: 'High Knees', sets: 3, reps: 50, rest: '60s' },
      ],
    },
  ],
};

// ============================================================================
// WEEKS 9-11: INTENSITY PHASE (ADVANCED VARIATIONS)
// ============================================================================

const intensityUpperBody: WorkoutTemplateDay = {
  name: 'Advanced Upper Body',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Band Face Pulls', sets: 2, reps: 15, format: 'prep', details: 'Or towel variation' },
        { name: 'Push-Up to T', sets: 2, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Advanced Push',
      type: 'strength',
      exercises: [
        { name: 'Pseudo Planche Push-Ups', sets: 4, reps: 8, intensity: 'Advanced', rest: '90s', details: 'Hands by hips, lean forward' },
        { name: 'Archer Push-Ups', sets: 4, reps: 8, intensity: 'Advanced', rest: '90s', details: 'Each side alternating' },
        { name: 'Handstand Push-Ups', sets: 4, reps: '5-8', intensity: 'Advanced', rest: '120s', details: 'Wall-assisted if needed' },
      ],
    },
    {
      name: 'Advanced Pull',
      type: 'strength',
      exercises: [
        { name: 'Weighted Pull-Ups', sets: 4, reps: 8, intensity: 'Progressive', rest: '120s', details: 'Backpack with books/water if no vest' },
        { name: 'Typewriter Pull-Ups', sets: 3, reps: 6, intensity: 'Advanced', rest: '90s', details: 'Slide side-to-side at top' },
      ],
    },
    {
      name: 'Advanced Core',
      type: 'core',
      exercises: [
        { name: 'Dragon Flags', sets: 3, reps: 6, intensity: 'Advanced', rest: '90s', details: 'Or negatives' },
        { name: 'L-Sit to V-Sit', sets: 3, reps: 8, intensity: 'Advanced', rest: '60s' },
      ],
    },
  ],
};

const intensityLowerBody: WorkoutTemplateDay = {
  name: 'Advanced Lower Body',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cossack Squats', sets: 2, reps: 10, format: 'prep', details: 'Each side' },
        { name: 'Ankle Bounces', sets: 1, reps: 30, format: 'prep' },
      ],
    },
    {
      name: 'Advanced Lower',
      type: 'strength',
      exercises: [
        { name: 'Pistol Squats', sets: 4, reps: 10, intensity: 'Advanced', rest: '90s', details: 'Full ROM each leg' },
        { name: 'Shrimp Squats', sets: 4, reps: 8, intensity: 'Advanced', rest: '90s', details: 'Each leg - rear foot to glute' },
        { name: 'Broad Jumps', sets: 4, reps: 8, intensity: 'Explosive', rest: '90s', details: 'Max distance, stick landing' },
      ],
    },
    {
      name: 'Plyometrics',
      type: 'strength',
      exercises: [
        { name: 'Box Jumps', sets: 4, reps: 10, intensity: 'Explosive', rest: '60s', details: 'Use stairs or sturdy surface' },
        { name: 'Single-Leg Bounds', sets: 3, reps: 20, intensity: 'Explosive', rest: '90s', details: 'Total reps - power focus' },
      ],
    },
  ],
};

const intensityConditioning: WorkoutTemplateDay = {
  name: 'Advanced Conditioning',
  sessionType: 'pt',
  estimatedDuration: 40,
  blocks: [
    {
      name: 'Tabata Protocol',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Tabata Circuit', 
          sets: 8, 
          reps: '20s work / 10s rest', 
          format: 'intervals',
          details: 'Exercise 1: Burpees | Exercise 2: Jump Squats | Alternate for 8 rounds total' 
        },
      ],
    },
    {
      name: 'Advanced AMRAP',
      type: 'conditioning',
      exercises: [
        { 
          name: '10 Minute AMRAP', 
          duration: '10:00', 
          format: 'amrap',
          details: '10 Push-Ups, 15 Air Squats, 20 Mountain Climbers - max rounds' 
        },
      ],
    },
  ],
};

// ============================================================================
// WEEK 12: FINAL TESTING
// ============================================================================

// W12 final retest split into Upper + Lower days to mirror W1's
// Upper/Lower structure. Five max-effort tests stacked in one 60-min
// day produced noisy benchmarks vs. W1 (where they were already split),
// so the user couldn't tell whether week-12 improvements were real or
// just fresher-vs-tired ordering effects.

const week12TestingUpper: WorkoutTemplateDay = {
  name: 'Final Assessment — Upper',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '8 min', format: 'prep' },
      ],
    },
    {
      name: 'Upper Final Testing',
      type: 'assessment',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', intensity: 'Max', details: 'Compare to Week 1', sessionType: 'assessment' },
        { name: 'Pull-Ups', sets: 1, reps: 'AMRAP', intensity: 'Max', details: 'Compare to Week 1', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, duration: 'Max time', intensity: 'Max', details: 'Compare to Week 1', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        { name: 'Stretching', duration: '8 min', format: 'recovery', details: 'Chest, shoulders, core' },
      ],
    },
  ],
};

const week12TestingLower: WorkoutTemplateDay = {
  name: 'Final Assessment — Lower',
  sessionType: 'assessment',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '8 min', format: 'prep' },
      ],
    },
    {
      name: 'Lower Final Testing',
      type: 'assessment',
      exercises: [
        { name: 'Air Squats', sets: 1, reps: 'AMRAP 2min', intensity: 'Max', details: 'Compare to Week 1', sessionType: 'assessment' },
        { name: 'Pistol Squats', sets: 1, reps: 'AMRAP', intensity: 'Max', details: 'Each leg — new skill test', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Celebration Cool Down',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '15 min', format: 'recovery', details: 'You earned this! Reflect on progress.' },
      ],
    },
  ],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const bodyweightFoundation12Week: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [],
  templates: {
    // Week 1: Assessment
    'week1-day1': week1Day1,
    'week1-day2': week1Day2,
    'week1-day3': foundationConditioning,
    'week1-day4': foundationActive,

    // Weeks 2-4: Foundation
    'week2-4-day1': foundationUpperBody,
    'week2-4-day2': foundationLowerBody,
    'week2-4-day3': foundationConditioning,
    'week2-4-day4': foundationActive,

    // Weeks 5-8: Volume
    'week5-8-day1': volumeUpperBody,
    'week5-8-day2': volumeLowerBody,
    'week5-8-day3': volumeConditioning,
    'week5-8-day4': foundationActive,

    // Weeks 9-11: Intensity
    'week9-11-day1': intensityUpperBody,
    'week9-11-day2': intensityLowerBody,
    'week9-11-day3': intensityConditioning,
    'week9-11-day4': foundationActive,

    // Week 12: Final Test — Upper retest day 1, Lower retest day 2,
    // active recovery on days 3-4. Mirrors W1's Upper/Lower split.
    'week12-day1': week12TestingUpper,
    'week12-day2': week12TestingLower,
    'week12-day3': foundationActive,
    'week12-day4': foundationActive,
  },
};

